import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTheme } from "@mui/material/styles";
import { api } from "../../api";

dayjs.extend(relativeTime);

// Human-readable label per notification type.
const TYPE_LABEL = {
  newIncident: "New incident reported",
  emergency: "🚨 Emergency incident",
  deAnonRequest: "De-anonymization requested",
  incidentAssigned: "Incident assigned to you",
  incidentUpdated: "Incident updated",
  newCommentResponder: "New comment on a report",
  newCommentStudent: "New message from a student",
};

const NotificationBell = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const open = Boolean(anchorEl);

  const fetchNotifications = async () => {
    try {
      const res = await api("get", {}, "Notifications");
      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        // Newest first.
        list.sort((a, b) => String(b.dateCreated || "").localeCompare(String(a.dateCreated || "")));
        setNotifications(list);
      }
    } catch (e) {
      // non-fatal — the bell simply shows no notifications
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Light polling so the badge stays current (also refreshes after opening an incident, which
    // clears its notification server-side).
    const interval = setInterval(fetchNotifications, 45000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleOpen = (e) => {
    fetchNotifications(); // ensure the list is fresh when opened
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (notification) => {
    handleClose();
    // Optimistically remove; opening the incident clears it server-side (detailsView).
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (notification.objectType === "incident" && notification.objectId) {
      navigate(`/incident/${notification.objectId}`);
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="large" aria-label="notifications">
        <Badge badgeContent={notifications.length} color="error">
          {/* Filled bell in a strong blue (primary.dark) — high contrast on the light/transparent
              topbar. The earlier white/light-blue outline icon was hard to see. */}
          <NotificationsIcon
            sx={{ color: theme.palette.primary.dark || "#0070c2", fontSize: 28 }}
          />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 420 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography sx={{ fontSize: "14px", color: theme.palette.text?.secondary || "#666" }}>
              No new notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => handleNotificationClick(n)}
              sx={{ display: "block", whiteSpace: "normal", py: 1.25 }}
            >
              <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                {TYPE_LABEL[n.type] || "Notification"}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: theme.palette.text?.secondary || "#666" }}>
                {n.objectLabel ? `Incident #${n.objectLabel}` : "View incident"}
                {n.dateCreated ? ` · ${dayjs(n.dateCreated).fromNow()}` : ""}
              </Typography>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
